<template>
  <div>
    <v-dialog
      v-model="show"
      content-class="crud-dialog"
      persistent
      max-width="400px"
    >
      <v-card v-if="!deleteCheck" class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11" class="text-left">
              <h2 v-if="isEditForm" class="text-h5">
                Edit {{ personFirstName }}
              </h2>
              <h2 v-else class="text-h5">Add new {{ relationName }}</h2>
            </v-col>
            <v-col cols="1">
              <v-icon class="close" color="black" @click.stop="closeDialog">{{
                icons.mdiClose
              }}</v-icon>
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text v-if="show" class="crud-card-text">
          <div
            v-if="showExistingPeople && !$apollo.loading"
            class="text-center mb-6"
          >
            <p>Select an existing person or add a new person</p>
            <PersonDialogExistingPeople
              :existing-people-list="existingPeopleList"
              :relation="relation"
              :testator="testator"
              :create-new-relationship="createNewRelationship"
              :decisionMaker="decisionMaker"
              :emergencyContacts="emergencyContacts"
              @existingPersonSelected="existingPersonSelected"
              @closeDialog="closeDialog"
              @errorThrown="errorThrown"
              @existingPersonSelectedWithMissingData="
                existingPersonSelectedWithMissingData
              "
            />
          </div>

          <h3 v-if="showExistingPeople" class="new-person-headline mt-8 mb-8">
            Add a person
          </h3>

          <PersonDialogForm
            ref="form"
            :person-to-update="personToUpdate"
            :relation="relation"
            :testator="testator"
            :person-first-name="personFirstName"
            :create-new-person="createNewPerson"
            :show-delete="showDelete"
            :update-missing-data-before-create-relation="
              updateMissingDataBeforeCreateRelation
            "
            @deleteCheck="deleteCheck = true"
            @closeDialog="closeDialog"
            @errorThrown="errorThrown"
            @newPersonAdded="newPersonAdded"
            @decisionMakerDialogShow="decisionMakerDialogShow"
            :personCreated="personCreated"
            :decisionMaker="decisionMaker"
            :emailRequired="addPersonEmailRequired"
            :emergencyContacts="emergencyContacts"
          />
        </v-card-text>
      </v-card>

      <DeleteDialog
        v-else
        :content="deleteDialogContent"
        :hide-delete-button="hideDeleteButton"
        @delete="deleteTestatorRelationship"
        @dialogClosed="closeDialog"
      />
    </v-dialog>

    <DecisionMakerDialog
      :decisionMakerDialog="decisionMakerDialog"
      :decisionMakerObject="decisionMakerObject"
      @decisionMakerDialogHide="decisionMakerDialogHide"
    />
    <ErrorSnackbar v-model="showError" :error="errorMessage" />
  </div>
</template>

<script>
  import { DELETE_TESTATOR_RELATIONSHIP_MUTATION } from '../../constants/graphql/people';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import DeleteDialog from '../common/DeleteDialog.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import PersonDialogExistingPeople from './PersonDialogExistingPeople.vue';
  import PersonDialogForm from './PersonDialogForm.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { userFacingGraphQLError } from '../../helpers/userFacingGraphQLError';
  import DecisionMakerDialog from '../common/dialogs/DecisionMakerDialog.vue';

  export default {
    name: 'PersonDialog',
    components: {
      DeleteDialog,
      ErrorSnackbar,
      PersonDialogExistingPeople,
      PersonDialogForm,
      DecisionMakerDialog,
    },
    mixins: [formHelperMixin],
    props: {
      relation: { type: String, required: true },
      displayExistingPeople: { type: Boolean, default: true },
      peopleListForDialog: { type: Array },
      value: { type: Boolean },
      personToUpdate: { type: Object },
      createNewRelationship: { type: Boolean, default: true },
      createNewPerson: { type: Boolean, default: true },
      decisionMaker: {
        required: false,
        default: false,
        type: [String, Boolean],
      },
      emergencyContacts: {
        required: false,
        default: false,
        type: [String, Boolean],
      },
      showDelete: { type: Boolean, default: true, required: false },
      addPersonEmailRequired: {
        type: Boolean,
        default: false,
        required: false,
      },
    },
    data() {
      return {
        deleteCheck: false,
        personFirstName: null,
        testator: {},
        errorMessage: null,
        updateMissingDataBeforeCreateRelation: false,
        decisionMakerObject: null,
        decisionMakerDialog: false,
      };
    },
    computed: {
      show: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },
      deleteDialogContent() {
        return {
          title: 'Remove ' + this.personFirstName,
          text: this.deleteCheckText,
          buttonText: 'Yes, Remove ' + this.toCapitalCase(this.personFirstName),
        };
      },
      deleteCheckText() {
        if (this.relation == 'HEIR') {
          if (this.personHasRemainderOfEstateSpecificPercentage) {
            return (
              'You have left a part of the remainder of your estate to ' +
              this.personToUpdate.fullName +
              ', please amend the Remainder of Estate section before deleting ' +
              this.personToUpdate.fullName +
              ' as an heir.'
            );
          } else {
            return '<b>WARNING:</b> If you delete this person any donations to them will also be deleted.<br/>Are you sure you want to delete this heir?';
          }
        } else {
          if (this.relationName.match('^[aieouhAIEOUH].*')) {
            return (
              'Are you sure you want to remove this person as an ' +
              this.relationName +
              '?'
            );
          } else {
            return (
              'Are you sure you want to remove this person as a ' +
              this.relationName +
              '?'
            );
          }
        }
      },
      personHasRemainderOfEstateSpecificPercentage() {
        return (
          this.personToUpdate.distributionSpecificPercentage ||
          this.personToUpdate.alternativeDistributionSpecificPercentage ||
          this.personToUpdate.distributionSpecificPercentageAlternatives
            .length > 0 ||
          this.personToUpdate
            .distributionSpecificPercentageAlternativeBeneficiaries?.length > 0
        );
      },
      hideDeleteButton() {
        return this.relation == 'HEIR' &&
          this.personHasRemainderOfEstateSpecificPercentage
          ? true
          : false;
      },
      showExistingPeople() {
        return (
          this.displayExistingPeople &&
          !this.isEditForm &&
          this.existingPeopleList &&
          this.existingPeopleList.length > 0
        );
      },
      isEditForm() {
        return this.personToUpdate && this.personToUpdate.id ? true : false;
      },
      relationName() {
        switch (this.relation) {
          case 'INSURANCE_NOMINATED_BENEFICIARY':
            return 'nominated beneficiary';
          case 'CO_EXECUTOR':
            return 'co-executor';
          default:
            return this.relation.toLowerCase().replace(/_/g, ' ');
        }
      },
      existingPeopleList() {
        return this.peopleListForDialog
          ? this.peopleListForDialog
          : this.testator.people;
      },
    },
    watch: {
      personToUpdate: function (newValue) {
        if (newValue.id)
          this.personFirstName = this.personToUpdate.fullName.split(' ')[0];
      },
    },
    created() {
      if (this.personToUpdate && this.personToUpdate.id) {
        this.personFirstName = this.personToUpdate.fullName.split(' ')[0];
      }
    },
    methods: {
      existingPersonSelected(person) {
        this.$emit('existingPersonSelected', person);
      },
      newPersonAdded(person) {
        this.$emit('newPersonAdded', person);
      },
      personCreated(person) {
        this.$emit('personCreated', person);
      },
      existingPersonSelectedWithMissingData(person) {
        this.$emit('existingPersonSelectedWithMissingData', person);
        this.updateMissingDataBeforeCreateRelation = true;
      },
      errorThrown(errorMessage) {
        this.errorMessage = errorMessage;
        this.displayErrorAndCloseDialog();
      },
      deleteTestatorRelationship() {
        const testatorRelationship =
          this.personToUpdate.testatorRelationships.find(
            tr => tr.relation == this.relation
          );
        const id = testatorRelationship.id;

        if (this.relation === 'LIVING_WILL_DECISION_MAKER') {
          this.$emit('decisionMakerRemoved');
        }

        this.$apollo
          .mutate({
            mutation: DELETE_TESTATOR_RELATIONSHIP_MUTATION,
            variables: {
              id,
            },
            update: (cache, { data: { deleteTestatorRelationship } }) => {
              this.updateCacheAfterDeleteTestatorRelationship(
                cache,
                deleteTestatorRelationship
              );
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.errorMessage = userFacingGraphQLError(error);
            this.displayErrorAndCloseDialog();
            if (!this.errorMessage) throw error;
          });
      },
      updateCacheAfterDeleteTestatorRelationship(
        cache,
        deleteTestatorRelationship
      ) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        if (deleteTestatorRelationship.personId) {
          // if person deleted delete person
          var people = data.testator.people;
          var personIndex = people.findIndex(
            p => p.id == deleteTestatorRelationship.personId
          );
          people.splice(personIndex, 1);
        } else {
          // else only delete testatorRelationship
          var person = data.testator.people.find(
            p => p.id == this.personToUpdate.id
          );
          var testatorRelationshipIndex =
            person.testatorRelationships.findIndex(
              tr => tr.id == deleteTestatorRelationship.testatorRelationshipId
            );
          person.testatorRelationships.splice(testatorRelationshipIndex, 1);
        }

        // Delete specific donations from cache if any deleted
        if (deleteTestatorRelationship.specificDonations) {
          for (const donationId of deleteTestatorRelationship.specificDonations) {
            const donationIndex = data.testator.specificDonations.findIndex(
              s => s.id == donationId
            );
            data.testator.specificDonations.splice(donationIndex, 1);
          }
        }

        cache.writeQuery({ ...query, data: data });
      },
      closeDialog() {
        this.show = false;
        setTimeout(() => this.clearDialog(), 300);
      },
      clearDialog() {
        this.$emit('modalClosed');
        if (this.$refs.form && this.$refs.form.$refs.observer) {
          this.$refs.form.$refs.observer.reset();
        }
        this.deleteCheck = false;
        this.updateMissingDataBeforeCreateRelation = false;
      },
      decisionMakerDialogShow(decisionMakerObject) {
        this.decisionMakerObject = decisionMakerObject;
        this.closeDialog();
        this.$nextTick(() => (this.decisionMakerDialog = true));
      },
      decisionMakerDialogHide() {
        this.decisionMakerDialog = false;
      },
      toCapitalCase(str) {
        return str
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      },
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
        fetchPolicy: 'cache',
      },
    },
  };
</script>
